import type { Photo, PrintSize } from './types';
import { FRAME_OPTIONS_BY_ID } from './constants';

export class PricingError extends Error {}

/** Recommended print DPI — sizes at or above this are sharp. */
export const RECOMMENDED_PRINT_DPI = 300;
/** Default threshold — below this a print looks visibly soft. Sizes can set
 * their own, and a photo–size pairing can override it (see print_sizes). */
export const MIN_PRINT_DPI = 150;

/**
 * Effective DPI when `photo`'s pixels are stretched across `size`'s physical
 * inches. Orientation-agnostic: pixels and inches are each sorted long/short so
 * a landscape image maps to a portrait size correctly. Returns the limiting
 * (lowest) DPI of the two axes — that's what governs perceived sharpness.
 */
export function effectivePrintDpi(
  photo: Pick<Photo, 'width' | 'height'>,
  size: Pick<PrintSize, 'widthIn' | 'heightIn'>
): number {
  const pxLong = Math.max(photo.width, photo.height);
  const pxShort = Math.min(photo.width, photo.height);
  const inLong = Math.max(size.widthIn, size.heightIn);
  const inShort = Math.min(size.widthIn, size.heightIn);
  if (!inLong || !inShort) return 0;
  return Math.floor(Math.min(pxLong / inLong, pxShort / inShort));
}

export type PrintQuality = 'good' | 'acceptable' | 'low';

export function printQuality(
  photo: Pick<Photo, 'width' | 'height'>,
  size: Pick<PrintSize, 'widthIn' | 'heightIn'>,
  minDpi: number = MIN_PRINT_DPI
): PrintQuality {
  const dpi = effectivePrintDpi(photo, size);
  if (dpi >= RECOMMENDED_PRINT_DPI) return 'good';
  if (dpi >= minDpi) return 'acceptable';
  return 'low';
}

export function priceForCents(
  photo: Pick<Photo, 'priceBaseCents'>,
  size: Pick<PrintSize, 'multiplier'>,
  frameId: string
): number {
  const frame = FRAME_OPTIONS_BY_ID[frameId];
  if (!frame) throw new PricingError(`Unknown frame: ${frameId}`);
  return Math.round(photo.priceBaseCents * size.multiplier) + frame.priceCents;
}

export function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}
